/**
 * BillingProviderStatusCard (OCP-575) - which provider takes new checkouts
 * and whether it is actually configured on the api. The mismatch used to
 * surface only as a customer's 503 at purchase time; this card (and the boot
 * check behind it) is where it shows first.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';

interface ProviderStatus {
  active: 'paddle' | 'polar';
  configured: { paddle: boolean; polar: boolean };
  active_configured: boolean;
  source: 'db' | 'default' | 'env';
  /** OCP-664 - the Polar-Version pin and where it stands on Polar's timeline. */
  polar_api_version?: string;
  polar_api_version_source?: 'db' | 'default' | 'env';
  polar_api_version_state?: 'next' | 'current' | 'deprecated' | 'removed';
  polar_api_version_current_until?: string;
  polar_api_version_removed_at?: string;
  /** OCP-661 - active products priced 0 at the provider; the checkout refuses them. */
  zero_price_products?: Array<{ product_id: string; display_name: string; local_price_eur_cents: number }>;
}

export function BillingProviderStatusCard() {
  const [status, setStatus] = useState<ProviderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api<ProviderStatus>('/v1/admin/billing-providers/status');
        if (!cancelled) setStatus(r);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? `Could not read the provider status (HTTP ${err.status}).` : 'Could not read the provider status.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div role="alert" className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }
  if (!status) return null;
  return (
    <div
      data-testid="billing-provider-status"
      className={`rounded-lg border px-4 py-3 text-sm ${
        status.active_configured
          ? 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200'
          : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200'
      }`}
      role={status.active_configured ? undefined : 'alert'}
    >
      <span className="font-semibold">Checkouts go to {status.active}</span>
      <span className="ml-1 text-xs text-gray-500">({status.source === 'db' ? 'operator setting' : status.source})</span>
      {status.active_configured ? (
        <span className="ml-2">and the {status.active} client is configured on this api.</span>
      ) : (
        <span className="ml-2">
          but the {status.active} client is NOT configured on this api - every self-serve checkout answers 503
          until <code>billing.active_provider</code> names a wired provider (paddle: {status.configured.paddle ? 'configured' : 'missing'},
          polar: {status.configured.polar ? 'configured' : 'missing'}).{' '}
          <Link to="/settings" className="underline">Settings</Link>
        </span>
      )}
      {status.polar_api_version && (
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400" data-testid="polar-api-version">
          Polar API pinned to <code>{status.polar_api_version}</code> ({status.polar_api_version_source === 'db' ? 'operator setting' : 'default'}) -{' '}
          {status.polar_api_version_state === 'current' && `current until ${status.polar_api_version_current_until}, removed ${status.polar_api_version_removed_at}`}
          {status.polar_api_version_state === 'deprecated' && (
            <span className="font-semibold text-amber-700 dark:text-amber-400">deprecated, removed {status.polar_api_version_removed_at} - migrate (billing.polar_api_version, OCP-665)</span>
          )}
          {status.polar_api_version_state === 'removed' && <span className="font-semibold text-red-700 dark:text-red-300">removed by Polar - requests fail until billing.polar_api_version names a live version</span>}
          {status.polar_api_version_state === 'next' && 'a preview version - may change at any time'}
        </div>
      )}
      {(status.zero_price_products?.length ?? 0) > 0 && (
        <div role="alert" className="mt-2 text-xs font-semibold text-red-700 dark:text-red-300" data-testid="zero-price-products">
          Priced 0 at {status.active} while the local price is not, so the checkout refuses them with{' '}
          <code>product_price_unsynced</code> until they are synced:{' '}
          {status.zero_price_products!.map((p) => p.display_name).join(', ')}.{' '}
          <Link to="/products" className="underline">Products</Link>
        </div>
      )}
    </div>
  );
}
